import { Link } from "react-router-dom";
import {
  ArrowRight,
  Trophy,
  Sparkles,
  Globe,
  CheckCircle2,
  MapPin,
  Plane,
} from "lucide-react";
import Seo from "@/components/Seo";
import { SERVICE_IMAGES } from "@/lib/media";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Incentive Travel", path: "/incentive-travel" },
];

const PROCESS = [
  ["01", "Brief", "Headcount, budget per pax, destination preference, the storyline you want delegates to remember."],
  ["02", "Concept", "Themed proposal with creative direction, venue shortlist, signature experiences and a costed itinerary."],
  ["03", "Production", "Contracts, AV, F&B, entertainment, branded environments, photographers, gala set design."],
  ["04", "Delivery", "Dedicated on-ground programme manager, 24/7 ops support, post-event content package."],
];

const EXAMPLES = [
  {
    icon: Sparkles,
    title: "Themed Gala Nights",
    copy:
      "Private dinners inside the Cloud Forest dome at Gardens by the Bay, branded fireworks on Victoria Harbour, gala dinners under Dubai's SkyView rooftop with Burj Khalifa as backdrop.",
  },
  {
    icon: Trophy,
    title: "Adventure Activities",
    copy:
      "Scuba diving on Apo Reef, helicopter island-hopping in Palawan, hot-air ballooning over Cappadocia, desert dune-bashing in Dubai with sunset BBQ.",
  },
  {
    icon: Globe,
    title: "Exclusive Access",
    copy:
      "Private museum after-hours, Michelin-starred chef-table dinners, Formula-1 paddock access at the Abu Dhabi GP, behind-the-scenes Universal Studios experiences in Singapore.",
  },
];

const DESTINATIONS = [
  { name: "Dubai", note: "GCC scale + production budgets that justify the wow factor" },
  { name: "Singapore", note: "Polished, safe, photogenic — Cloud Forest, rooftops, Sentosa" },
  { name: "Bali / Thailand", note: "Beach incentives, luxury villa buyouts, gastronomy" },
  { name: "Philippines", note: "Private island buyouts — Amanpulo, El Nido — unmatched beach scenery" },
  { name: "Vietnam", note: "Ha Long Bay junk-boat overnights, Hanoi colonial charm" },
  { name: "Hong Kong", note: "Harbour-front gala dinners, junk-boat charters" },
];

const FAQS = [
  {
    q: "What makes a great incentive travel programme?",
    a: "Three things separate a memorable incentive trip from a forgettable one. First, signature moments — at least one experience your delegates cannot book themselves, and that they'll be talking about a year later (a private dinner inside a museum, a yacht charter, a chef's table). Second, polished delivery — flawless transfers, surprise upgrades, branded touches everywhere. Third, social-shareable content — pre-arranged photographers and short-form video so your top performers walk away with content they're proud to post. Trawelldmc designs all three into every programme.",
  },
  {
    q: "How does Trawelldmc design an incentive programme?",
    a: "Our process is four-phase: Brief (we understand headcount, budget, destination preference, and the narrative you want delegates to remember), Concept (themed proposal with creative direction, signature experiences and a costed itinerary), Production (contracts, AV, F&B, branded environments, photographers, gala staging), and Delivery (on-ground programme manager plus 24/7 operations). The full timeline is typically 4–8 weeks from brief to delivery for programmes under 100 pax; 4–6 months for premium 200+ pax programmes.",
  },
  {
    q: "Which destinations are best for incentive travel?",
    a: "It depends on the storyline. For beach incentives, the Philippines (Palawan, Boracay) and Thailand (Phuket) offer the most photogenic luxury inventory at competitive rates. For city-and-culture programmes, Singapore, Hong Kong and Vietnam combine polish with strong storyline. For scale and production budgets, Dubai is unmatched — Burj Khalifa rooftops, Atlantis the Royal, desert experiences and an inventory of 5-star hotels deep enough for 1,000-pax incentives. Bali offers Asia's most distinctive luxury villa buyout market for premium incentive groups of 50–200 pax.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function IncentiveTravel() {
  return (
    <div data-testid="incentive-travel-page">
      <Seo
        path="/incentive-travel"
        title="Incentive Travel Programmes — Asia & Middle East | Trawelldmc"
        description="Design bespoke incentive travel programmes with Trawelldmc. Southeast Asia and Dubai incentive specialists. Motivate top performers with unforgettable experiences."
        breadcrumb={BREADCRUMB}
        extraJsonLd={FAQ_JSONLD}
      />

      <section className="relative">
        <img
          src={SERVICE_IMAGES.incentive}
          alt="Trawelldmc incentive travel programmes for high performers"
          width="1920"
          height="1080"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(11,25,44,0.92) 0%, rgba(11,25,44,0.55) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-44 text-white">
          <div className="max-w-3xl">
            <div className="overline !text-[#FF5238] mb-5">Incentive Travel Specialists</div>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.04]">
              Incentive Travel — <br />
              <span className="text-[#FF5238]">Bespoke programmes</span> for high performers.
            </h1>
            <p className="text-white/80 mt-8 text-lg max-w-2xl leading-relaxed">
              Reward, motivate and retain your top performers with incentive
              programmes engineered across seven world-class destinations.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/inquiry?type=Incentive" className="btn-primary" data-testid="inc-cta-rfp">
                Submit an RFP <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/quote" className="btn-secondary" data-testid="inc-cta-ai-quote">Instant AI estimate</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">What makes a great incentive</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Signature moments. <br />Polished delivery. Shareable content.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-700 text-lg leading-relaxed">
            <p>
              An incentive trip works when delegates come back to the office
              with one moment they cannot stop talking about. That moment is
              the entire ROI on the programme — the reason your top performer
              chases the number again next quarter, and the reason their
              colleagues compete to qualify next year.
            </p>
            <p>
              Trawelldmc designs incentive programmes around three principles.
              Signature moments — at least one experience your delegates
              cannot book themselves (a private museum dinner, a yacht
              charter, a chef's table at a destination restaurant). Polished
              delivery — flawless transfers, surprise upgrades, branded
              touches everywhere from welcome amenities to gift bags. And
              shareable content — pre-arranged photographers, drone footage,
              short-form video so your performers walk away with social
              content they're proud to post.
            </p>
            <p>
              We've designed and delivered incentive programmes ranging from
              30-pax CEO Club retreats at Amanpulo to 600-pax dealer reward
              trips across Dubai. The same principles apply at every scale —
              concept, production, delivery, content. Owned end-to-end by
              one accountable team.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">Our design process</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              From brief to delivery — <br />in four moves.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {PROCESS.map(([n, t, c]) => (
              <div key={n} className="border-t-2 border-[#FF5238] pt-6">
                <div className="font-heading text-5xl font-light text-[#FF5238]">{n}</div>
                <div className="font-heading text-xl font-medium mt-3 text-[#0B192C]">{t}</div>
                <p className="text-slate-600 text-sm mt-3 leading-relaxed">{c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">Example experiences</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            The kind of moments <br />we engineer.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {EXAMPLES.map((e) => (
            <div key={e.title} className="card-flat p-8">
              <e.icon className="h-8 w-8 text-[#FF5238]" />
              <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">{e.title}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{e.copy}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline !text-[#FF5238] mb-4">Best destinations</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              Best destinations <br /><span className="text-[#FF5238]">for incentives.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
            {DESTINATIONS.map((d) => (
              <div key={d.name} className="border-t border-white/15 pt-5">
                <div className="flex gap-3 items-start">
                  <CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-heading text-lg font-medium">{d.name}</div>
                    <div className="text-sm text-white/60 mt-1">{d.note}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">FAQ</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Frequently Asked Questions
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {FAQS.map((f, i) => (
            <div key={f.q} data-testid={`inc-faq-${i}`}>
              <h3 className="font-heading text-xl font-medium text-[#0B192C] tracking-tight">{f.q}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Next step</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Plan your next <br /><span className="text-[#FF5238]">incentive trip.</span>
            </h2>
            <p className="text-slate-600 mt-6 max-w-xl text-lg leading-relaxed">
              Tell us the headcount, budget and destination preference — we'll
              respond within 48 hours with a themed proposal.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link to="/inquiry?type=Incentive" className="btn-primary w-full justify-center" data-testid="inc-bottom-cta">
              Submit an RFP <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="btn-secondary w-full justify-center">Contact our team</Link>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16">
          <div className="overline mb-6">Explore related</div>
          <nav className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/mice-services" className="group flex items-start gap-3">
              <Trophy className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">MICE Services</div>
                <div className="text-sm text-slate-500 mt-1">Meetings, conferences, exhibitions</div>
              </div>
            </Link>
            <Link to="/corporate-travel" className="group flex items-start gap-3">
              <Trophy className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">Corporate Travel</div>
                <div className="text-sm text-slate-500 mt-1">Group travel management</div>
              </div>
            </Link>
            <Link to="/philippines-dmc" className="group flex items-start gap-3">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Philippines</div>
                <div className="text-sm text-slate-500 mt-1">Island incentives — Palawan & Cebu</div>
              </div>
            </Link>
            <Link to="/" className="group flex items-start gap-3">
              <Plane className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">Trawelldmc Home</div>
                <div className="text-sm text-slate-500 mt-1">All destinations & services</div>
              </div>
            </Link>
          </nav>
        </div>
      </section>
    </div>
  );
}
